import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadPDF from "components/Shared/DownloadPDF"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { exportData } from "store/actions/commonAction"
import {
  deleteWalletCoupon,
  getAllWalletCoupons,
  updateWalletCouponStatus,
} from "store/actions/promotionsAction"
import { showAlert } from "store/reducers/alertReducer"
import ConfirmModal from "components/Common/ConfirmModal"

const CouponRedeem = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Wallet Coupons List") + " "
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const navigate = useNavigate()

  const {
    loading,
    couponsItemsPerPage: itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isUpdating,
    isLoading,
    coupons,
    couponLoading,
  } = useSelector(state => state.Promotions)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedId, setSelectedId] = useState(null)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const { userRole, accountType } = useSelector(state => state.Login)
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
  })
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusCouponId, setStatusCouponId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchAllCouponRedeem()
  }, [])

  const fetchAllCouponRedeem = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllWalletCoupons(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page)

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }
    dispatch(getAllWalletCoupons(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/wallet-coupons?${searchParams.toString()}`)
        }
      })
      .catch(error => {})
  }

  const handleSearch = () => {
    getApiCall(1)
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 2,
      keyword: "",
    }
    dispatch(getAllWalletCoupons(payload))

    setSelectedFilters({
      status: 2,
      sortOrder: 2,
    })
    setSearchTerm("")
    navigate(`/wallet-coupons?page=${1}`)
  }

  const handleConfirmStatusUpdate = () => {
    if (!statusCouponId) return
    setSelectedId(statusCouponId)
    const payload = {
      id: statusCouponId,
      status: statusToUpdate,
    }
    dispatch(updateWalletCouponStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusCouponId(null)
        setStatusToUpdate(null)
      })
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2",
    }
    dispatch(deleteWalletCoupon(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDownloadPDF = () => {
    const { status } = selectedFilters

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON",
      exportType: "pdf",
      walletCoupon: true,
    }

    dispatch(exportData(payload))
      .then(action => {
        const blobData = action.payload
        DownloadPDF(blobData, t("Wallet Coupons"))
      })
      .catch(error => {})
  }

  const exportToCSV = async () => {
    const { status } = selectedFilters

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON_REDEEM",
      exportType: "csv",
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      DownloadCSV(response, t("Wallet Coupons"))
    }
  }

  const handleExcelDownload = async () => {
    const { status } = selectedFilters

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "COUPON_REDEEM",
      exportType: "xlsx",
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      DownloadXLSX(response, t("Wallet Coupons"))
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Wallet Coupons List")}
                isAddBtn={usersPermissions.create}
                addBtnText={t("Add Wallet Coupon")}
                onAddBtnClick={() => navigate(`/wallet-coupons/add`)}
                isExportData={couponLoading ? false : usersPermissions.create}
                onDownloadPDF={handleDownloadPDF}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Enter coupon code or name")}
                  colSize={3}
                />
                <div className="">
                  {couponLoading ? (
                    <TableShimmer row={7} col={7} />
                  ) : coupons.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No wallet coupons found")}
                    </p>
                  ) : (
                    <Table className="table mb-0 capitalize_td" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Coupon Code")}</th>
                          <th>{t("Starts At")}</th>
                          <th>{t("Expires At")}</th>
                          <th>{t("Usage Count")}</th>
                          <th>{t("Last Update By")}</th>
                          <th>{t("Created By")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() =>
                              navigate(
                                `/wallet-coupons/coupon-details?id=${item._id}`
                              )
                            }
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * 10 + index + 1}
                            </th>
                            <td>
                              {language === "sa" ? item.nameAr : item.name}
                            </td>
                            <td>{item.code || "-"}</td>
                            <td>{formatDateTime(item.startsAt)}</td>
                            <td>{formatDateTime(item.expiresAt)}</td>
                            <td>
                              {item?.redemptionsCount ?? 0 ? (
                                <span>{item.redemptionsCount}</span>
                              ) : (
                                0
                              )}
                            </td>
                            <td>
                              {item?.lastUpdatedBy
                                ? `${item?.lastUpdatedBy?.firstName || ""} ${
                                    item?.lastUpdatedBy?.lastName || ""
                                  }`.trim()
                                : "-"}
                            </td>
                            <td>
                              {item?.createdBy
                                ? `${item?.createdBy?.firstName || ""} ${
                                    item?.createdBy?.lastName || ""
                                  }`.trim()
                                : ""}
                            </td>
                            <td>
                              {item.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      setStatusCouponId(item._id)
                                      setStatusToUpdate(
                                        item.status === 1 ? "0" : "1"
                                      )
                                      setShowStatusModal(true)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isUpdating
                                    }
                                  />
                                )}
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="card-drop"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    {usersPermissions.edit && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/wallet-coupons/edit?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                          `/wallet-coupons/coupon-details?id=${item._id}`
                                        )
                                      }}
                                    >
                                      <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                      {t("View")}
                                    </DropdownItem>

                                    {usersPermissions.delete && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(item._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!couponLoading && (
                  <div className="mt-3">
                    <Paginations
                      data={coupons}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Wallet Coupons"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isUpdating}
        onDeleteClick={handleConfirmStatusUpdate}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </div>
  )
}

export default CouponRedeem
